import type { SpeciesId } from "./species.ts";
import type { EncounterRow, PatchId, ZoneId } from "./map.ts";
import type { DayPeriod } from "../world/fx.ts";

/**
 * World secrets: who comes out at dawn, dusk and night, and the six legend hunts.
 *
 * Visitors: every wild zone has two night rows, one dawn row and one dusk row of memelings that
 * normally live in another region (night leans REKT / MOON / RUG / AI, dawn STAKE / HODL / SIGNAL,
 * dusk BURN / FLEX / COPE), at the zone's own levels, each about 7% of the table. Daytime grass is
 * unchanged. Resting at a kettle lets the clock move on to the next part of the day.
 */
export const TIME_ROWS: Partial<Record<ZoneId, Partial<Record<DayPeriod, EncounterRow[]>>>> = {
  drippond: { dawn: [{ id: "vinesnek", w: 15, lo: 3, hi: 5 }], dusk: [{ id: "gasbull", w: 15, lo: 3, hi: 5 }], night: [{ id: "honeytoad", w: 15, lo: 3, hi: 5 }, { id: "deadcat", w: 15, lo: 3, hi: 5 }] },
  copeCliffs: { dawn: [{ id: "cachemole", w: 22, lo: 8, hi: 11 }], dusk: [{ id: "slothlet", w: 22, lo: 8, hi: 11 }], night: [{ id: "jellyfud", w: 22, lo: 8, hi: 11 }, { id: "mirebot", w: 22, lo: 8, hi: 11 }] },
  chartwood: { dawn: [{ id: "pengwallet", w: 31, lo: 7, hi: 10 }], dusk: [{ id: "origamint", w: 31, lo: 7, hi: 10 }], night: [{ id: "ledmoth", w: 31, lo: 7, hi: 10 }, { id: "craterling", w: 31, lo: 7, hi: 10 }] },
  gasGulch: { dawn: [{ id: "frostbag", w: 23, lo: 11, hi: 14 }], dusk: [{ id: "emberbit", w: 23, lo: 11, hi: 14 }], night: [{ id: "glitchpup", w: 23, lo: 11, hi: 14 }, { id: "moltwisp", w: 23, lo: 11, hi: 14 }] },
  yieldYard: { dawn: [{ id: "toucoin", w: 32, lo: 14, hi: 17 }], dusk: [{ id: "fanbit", w: 32, lo: 14, hi: 17 }], night: [{ id: "ruglet", w: 32, lo: 14, hi: 17 }, { id: "nodelet", w: 32, lo: 14, hi: 17 }] },
  candleDunes: { dawn: [{ id: "pumpkoin", w: 8, lo: 18, hi: 21 }], dusk: [{ id: "saucelord", w: 8, lo: 18, hi: 21 }], night: [{ id: "crowlet", w: 8, lo: 18, hi: 21 }, { id: "rektling", w: 8, lo: 18, hi: 21 }] },
  frostfloor: { dawn: [{ id: "golemint", w: 7, lo: 20, hi: 23 }], dusk: [{ id: "slaylord", w: 7, lo: 20, hi: 23 }], night: [{ id: "tombstonk", w: 7, lo: 20, hi: 23 }, { id: "rocketot", w: 7, lo: 20, hi: 23 }] },
  rugmire: { dawn: [{ id: "honkference", w: 8, lo: 22, hi: 25 }], dusk: [{ id: "auditlord", w: 8, lo: 22, hi: 25 }], night: [{ id: "nimbaa", w: 8, lo: 22, hi: 25 }, { id: "hashgrub", w: 8, lo: 22, hi: 25 }] },
  crystalCache: { dawn: [{ id: "locklord", w: 7, lo: 24, hi: 27 }], dusk: [{ id: "delulord", w: 7, lo: 24, hi: 27 }], night: [{ id: "wraplet", w: 7, lo: 24, hi: 27 }, { id: "webthree", w: 7, lo: 24, hi: 27 }] },
  magmaMint: { dawn: [{ id: "midbaron", w: 5, lo: 26, hi: 29 }], dusk: [{ id: "copiumax", w: 5, lo: 26, hi: 29 }], night: [{ id: "pendbat", w: 5, lo: 26, hi: 29 }, { id: "chatbit", w: 5, lo: 26, hi: 29 }] },
  mempoolCaverns: { dawn: [{ id: "nutling", w: 8, lo: 30, hi: 33 }], dusk: [{ id: "breadwinner", w: 8, lo: 30, hi: 33 }], night: [{ id: "swarmlord", w: 8, lo: 30, hi: 33 }, { id: "rektling", w: 8, lo: 30, hi: 33 }] },
  coralLedger: { dawn: [{ id: "leakmaw", w: 8, lo: 31, hi: 34 }], dusk: [{ id: "ghostwyrm", w: 8, lo: 31, hi: 34 }], night: [{ id: "nodelet", w: 8, lo: 31, hi: 34 }, { id: "rugwraith", w: 8, lo: 31, hi: 34 }] },
  neonGrid: { dawn: [{ id: "diamondpaws", w: 8, lo: 33, hi: 36 }], dusk: [{ id: "wickwolf", w: 8, lo: 33, hi: 36 }], night: [{ id: "deadcat", w: 8, lo: 33, hi: 36 }, { id: "wraplet", w: 8, lo: 33, hi: 36 }] },
  cloudChain: { dawn: [{ id: "humpfund", w: 7, lo: 35, hi: 38 }], dusk: [{ id: "napocalypse", w: 7, lo: 35, hi: 38 }], night: [{ id: "crowlet", w: 7, lo: 35, hi: 38 }, { id: "moltwisp", w: 7, lo: 35, hi: 38 }] },
  halvingHarvest: { dawn: [{ id: "lagomorph", w: 6, lo: 38, hi: 41 }], dusk: [{ id: "gravylord", w: 6, lo: 38, hi: 41 }], night: [{ id: "pearlclam", w: 6, lo: 38, hi: 41 }, { id: "lunawolf", w: 6, lo: 38, hi: 41 }] },
  ghostchain: { dawn: [{ id: "ratioking", w: 8, lo: 40, hi: 43 }], dusk: [{ id: "whalord", w: 8, lo: 40, hi: 43 }], night: [{ id: "craterling", w: 8, lo: 40, hi: 43 }, { id: "rocketitan", w: 8, lo: 40, hi: 43 }] },
  pumpJungle: { dawn: [{ id: "postwyrm", w: 7, lo: 42, hi: 45 }], dusk: [{ id: "shroomancer", w: 7, lo: 42, hi: 45 }], night: [{ id: "asicarab", w: 7, lo: 42, hi: 45 }, { id: "fanstorm", w: 7, lo: 42, hi: 45 }] },
  sakuraStake: { dawn: [{ id: "shardking", w: 6, lo: 44, hi: 47 }], dusk: [{ id: "fomobeast", w: 6, lo: 44, hi: 47 }], night: [{ id: "honeybaron", w: 6, lo: 44, hi: 47 }, { id: "mireshill", w: 6, lo: 44, hi: 47 }] },
  rugpullRuins: { dawn: [{ id: "pinglord", w: 7, lo: 46, hi: 49 }], dusk: [{ id: "lorelord", w: 7, lo: 46, hi: 49 }], night: [{ id: "stuckwing", w: 7, lo: 46, hi: 49 }, { id: "swarmlord", w: 7, lo: 46, hi: 49 }] },
};

/** Where and when a clue can be found: a sign or kettle by name, optionally only at one time of day. */
export interface Clue {
  id: string;
  /** Spot title (every sign and kettle title is unique). */
  spot: string;
  when?: DayPeriod;
  text: string;
}

/**
 * A legend hunt: three clues (a sign that can be read any time, then two kettles at particular
 * hours), then the legend waits in its lair bed at its hour until it is caught. Its usual one-in-a-
 * hundred row in the same bed stays, so a lucky scout can still meet it without the trail.
 */
export interface Hunt {
  legend: SpeciesId;
  zone: ZoneId;
  bed: PatchId;
  /** Human name of the lair, used in messages. */
  lair: string;
  when: DayPeriod;
  level: number;
  clues: readonly [Clue, Clue, Clue];
}

export const HUNTS: readonly Hunt[] = [
  {
    legend: "genesiwyrm", zone: "crystalCache", bed: "vault", lair: "the Crystal Cache's south vault", when: "dawn", level: 40,
    clues: [
      { id: "gen1", spot: "Crystal Cache", text: "Scratched under the sign: \"It stirs when the first light hits the ice.\"" },
      { id: "gen2", spot: "Frost Kettle", when: "night", text: "In the steam: a block of ice with a flame frozen inside it." },
      { id: "gen3", spot: "Vent Kettle", when: "dawn", text: "The vents cough up a glassy scale, cold on one side and hot on the other." },
    ],
  },
  {
    legend: "ursarug", zone: "rugmire", bed: "sink", lair: "the Rugmire's south sink", when: "night", level: 40,
    clues: [
      { id: "urs1", spot: "Rugmire", text: "Fresh paint below: \"The floor only gives way after dark.\"" },
      { id: "urs2", spot: "Bog Kettle", when: "dusk", text: "Something heavy rolls over in the south sink. The lid rattles twice." },
      { id: "urs3", spot: "Dune Kettle", when: "night", text: "Paw prints as wide as a shield, heading east into the bog." },
    ],
  },
  {
    legend: "neuralith", zone: "neonGrid", bed: "subnet", lair: "the Neon Grid's south subnet", when: "night", level: 40,
    clues: [
      { id: "neu1", spot: "Neon Grid", text: "A sticky note: \"It only computes when the grid goes dark.\"" },
      { id: "neu2", spot: "Grid Kettle", when: "dusk", text: "The kettle's display flickers: SUBNET ONLINE AT 21:00." },
      { id: "neu3", spot: "Shroom Kettle", when: "night", text: "The mushrooms pulse in a pattern. It spells S-O-U-T-H." },
    ],
  },
  {
    legend: "selenarch", zone: "cloudChain", bed: "nimbus", lair: "the Cloud Chain's south bank", when: "dusk", level: 40,
    clues: [
      { id: "sel1", spot: "Cloud Chain", text: "A silver feather tied to the post: \"The ledger opens at moonrise.\"" },
      { id: "sel2", spot: "Sky Kettle", when: "night", text: "An owl hoots far below the clouds, from the south bank." },
      { id: "sel3", spot: "Reef Kettle", when: "dusk", text: "Two silver crescents float in the tide pool, drifting toward the clouds." },
    ],
  },
  {
    legend: "arborealm", zone: "sakuraStake", bed: "koi", lair: "Sakura Stake's koi pond", when: "dawn", level: 50,
    clues: [
      { id: "arb1", spot: "Sakura Stake", text: "Carved in the wood: \"The stag drinks at the koi pond while the petals are wet with dawn.\"" },
      { id: "arb2", spot: "Blossom Kettle", when: "dusk", text: "A single petal floats on the tea. It smells like morning." },
      { id: "arb3", spot: "Harvest Kettle", when: "dawn", text: "Hoofprints in the dew, each one sprouting a tiny blossom, heading north-west." },
    ],
  },
  {
    legend: "rektquiem", zone: "rugpullRuins", bed: "vault", lair: "the Rugpull Ruins' vault", when: "night", level: 50,
    clues: [
      { id: "rek1", spot: "Rugpull Ruins", text: "Faint graffiti: \"At night the last song plays in the vault.\"" },
      { id: "rek2", spot: "Ruin Kettle", when: "dusk", text: "Organ notes drift up from the south of the ruins. Then silence." },
      { id: "rek3", spot: "Grave Kettle", when: "night", text: "A ghost asks the way to the ruins' vault, then forgets it asked." },
    ],
  },
];

export const CLUE_IDS: readonly string[] = HUNTS.flatMap((h) => h.clues.map((c) => c.id));

/** The clue this spot gives right now, if it has one the scout has not found yet. */
export function clueAt(spotTitle: string, period: DayPeriod, found: readonly string[]): { hunt: Hunt; clue: Clue } | null {
  for (const hunt of HUNTS) for (const clue of hunt.clues) {
    if (clue.spot === spotTitle && (!clue.when || clue.when === period) && !found.includes(clue.id)) return { hunt, clue };
  }
  return null;
}

export function huntComplete(hunt: Hunt, found: readonly string[]): boolean {
  return hunt.clues.every((c) => found.includes(c.id));
}

/** The legend waiting in this bed at this hour, once its trail is complete and until it is caught. */
export function lairAt(zone: ZoneId, bed: PatchId | null, period: DayPeriod, found: readonly string[], caught: readonly SpeciesId[]): Hunt | null {
  return HUNTS.find((h) => h.zone === zone && h.bed === bed && h.when === period && huntComplete(h, found) && !caught.includes(h.legend)) ?? null;
}
